import { useState, useCallback, useRef } from 'react';
import ordersService from '../services/ordersService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';
import useProductSizes from './useProductSizes';
import { useWarehouseContext } from '../context/WarehouseContext';
import { formatBulkDeleteMessage } from '../utils/format';


/**
 * Hook: useOrders
 * - Provides CRUD and list operations for orders
 * - References product sizes hook so UI can show product-size related data when creating an order
 */
const normalizeOrderPayload = (p = {}) => {
  const out = {};
  // Customer: if new-customer style fields present, put them flat at root level (not nested)
  const hasNewCustomer = p && (p.owner_name || p.company_name || p.contact_person);
  if (hasNewCustomer) {
    // New customer format: flat structure with all fields at root level
    out.owner_name = p.owner_name || '';
    out.company_name = p.company_name || '';
    out.gst_number = p.gst_number || p.gstNumber || null;
    out.contact_person = p.contact_person || '';
    out.mobile_number = p.mobile_number || p.phone_number_1 || p.phoneNumber1 || '';
    out.phone_number = p.phone_number || null;
    out.email = p.email || null;
    out.address_line1 = p.address_line1 || p.address_1 || '';
    out.address_line2 = p.address_line2 || null;
    out.city = p.city || '';
    out.state = p.state || '';
    out.country = p.country || '';
    out.zip_code = p.zip_code || p.zipCode || '';
    out.tally_name = p.tally_name || p.tallyName || '';
    out.reference_party_name = p.reference_party_name || p.referencePartyName || '';
    out.credit_period = (typeof p.credit_period !== 'undefined' && p.credit_period !== null && p.credit_period !== '') ? Number(p.credit_period) : null;
  } else if (p.customer_id || p.customerId) {
    out.customer_id = Number(p.customer_id ?? p.customerId);
  }

  // Basic order-level fields
  if (p.location_id || p.locationId) out.location_id = Number(p.location_id ?? p.locationId);
  if (p.expected_delivery_date) out.expected_delivery_date = p.expected_delivery_date;
  // This mapper is a WHITELIST: the edit form sent price_list_date but it was never
  // copied here, so "Price List Date" could be changed on screen and silently never
  // saved (creation bypasses this mapper, which is why it only failed on edit).
  if (p.price_list_date) out.price_list_date = p.price_list_date;
  if (typeof p.freight !== 'undefined' && p.freight !== null && p.freight !== '') out.freight = Number(p.freight);
  if (p.freight_type) out.freight_type = String(p.freight_type).toLowerCase();
  if (p.freight_pricing_type) out.freight_pricing_type = String(p.freight_pricing_type).toLowerCase();
  if (p.discount_type) out.discount_type = String(p.discount_type).toLowerCase();
  if (typeof p.discount_value !== 'undefined' && p.discount_value !== null && p.discount_value !== '') out.discount_value = Number(p.discount_value);
  if (typeof p.loading_cost_per_kg !== 'undefined' && p.loading_cost_per_kg !== null && p.loading_cost_per_kg !== '') out.loading_cost_per_kg = Number(p.loading_cost_per_kg);
  if (p.gst_option) out.gst_option = String(p.gst_option).toLowerCase();
  if (p.gstn_number) out.gstn_number = p.gstn_number;
  // Preserve explicit gst_included flag coming from forms
  if (typeof p.gst_included !== 'undefined') out.gst_included = !!p.gst_included;
  if (typeof p.sms_notification !== 'undefined') out.sms_notification = !!p.sms_notification;
  if (typeof p.remark !== 'undefined' && p.remark !== null) out.remark = String(p.remark);
  if (typeof p.order_internal_remark !== 'undefined' && p.order_internal_remark !== null) out.order_internal_remark = String(p.order_internal_remark);
  if (p.reference_party_name) out.reference_party_name = p.reference_party_name;
  if (p.payment_terms) out.payment_terms = String(p.payment_terms);
  // Preserve status when updating an order (don't reset it)
  if (p.status) out.status = p.status;

  // Source: support dropdown-provided `source` (object/string/number) or explicit supplier_id/warehouse_id
  if (p.source && typeof p.source === 'object' && p.source.type) {
    out.source_type = p.source.type;
    if (typeof p.source.id !== 'undefined' && p.source.id !== null && p.source.id !== '') {
      if (p.source.type === 'supplier') out.supplier_id = Number(p.source.id);
      if (p.source.type === 'warehouse') out.warehouse_id = Number(p.source.id);
    }
  } else if (p.source && typeof p.source === 'string') {
    const s = p.source.trim();
    const parts = s.split(':');
    if (parts.length === 2 && (parts[0] === 'supplier' || parts[0] === 'warehouse')) {
      out.source_type = parts[0];
      const id = parts[1];
      if (parts[0] === 'supplier') out.supplier_id = Number(id);
      if (parts[0] === 'warehouse') out.warehouse_id = Number(id);
    } else if (/^\d+$/.test(s)) {
      out.source_type = out.source_type || 'warehouse';
      out.warehouse_id = Number(s);
    } else {
      if (s === 'supplier' || s === 'warehouse') out.source_type = s;
    }
  } else if (typeof p.source === 'number') {
    out.source_type = out.source_type || 'warehouse';
    out.warehouse_id = Number(p.source);
  } else {
    if (p.source_type) out.source_type = p.source_type;
    else if (p.supplier_id || p.supplierId || p.supplier) out.source_type = 'supplier';
    else if (p.warehouse_id || p.warehouseId) out.source_type = 'warehouse';
    if (p.supplier_id || p.supplierId) out.supplier_id = Number(p.supplier_id ?? p.supplierId);
    if (p.warehouse_id || p.warehouseId) out.warehouse_id = Number(p.warehouse_id ?? p.warehouseId);
  }



  // Items mapping
  const itemsIn = Array.isArray(p.items) ? p.items : [];
  out.items = itemsIn.map(it => {
    const mapped = {};
    if (it.product_size_id || it.productSizeId) mapped.product_size_id = Number(it.product_size_id ?? it.productSizeId);
    mapped.unit = it.unit || it.units || it.uom || '';
    if (typeof it.length !== 'undefined' && it.length !== null && it.length !== '') mapped.length = Number(it.length);
    if (typeof it.length_note !== 'undefined') mapped.length_note = it.length_note === null ? null : String(it.length_note);
    if (typeof it.quantity !== 'undefined' && it.quantity !== null && it.quantity !== '') mapped.quantity = Number(it.quantity);
    if (typeof it.price !== 'undefined' && it.price !== null && it.price !== '') mapped.price = Number(it.price);
    if (typeof it.quantity_in_pieces !== 'undefined' && it.quantity_in_pieces !== null && it.quantity_in_pieces !== '') mapped.quantity_in_pieces = Number(it.quantity_in_pieces);
    else if (typeof it.pieces !== 'undefined' && it.pieces !== null && it.pieces !== '') mapped.quantity_in_pieces = Number(it.pieces);
    if (typeof it.gst_applicable !== 'undefined') mapped.gst_applicable = !!it.gst_applicable;
    else if (typeof p.include_gst !== 'undefined') mapped.gst_applicable = !!p.include_gst;
    if (typeof it.remark !== 'undefined' && it.remark !== null) mapped.remark = String(it.remark);
    mapped.load_by_pieces = !!(it.load_by_pieces);
    // Coils chosen for this line. This mapper is a WHITELIST — anything not copied here
    // never leaves the browser — so omitting coil_picks meant the order saved with no
    // allocations at all, the picker still showed the picks (local state), and
    // confirmation then refused the order with "no coils were selected".
    if (Array.isArray(it.coil_picks) && it.coil_picks.length) {
      mapped.coil_picks = it.coil_picks.map(pick => ({
        coil_id: Number(pick.coil_id),
        take_kg: Number(pick.take_kg),
        ...(pick.remainder_kg === undefined || pick.remainder_kg === null
          ? {}
          : { remainder_kg: Number(pick.remainder_kg) }),
      }));
    }
    return mapped;
  }).filter(i => typeof i.product_size_id !== 'undefined');

  return out;
};

const useOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [vehicleTracking, setVehicleTracking] = useState([]);
  const [vehicleTrackingTotal, setVehicleTrackingTotal] = useState(0);
  const { selectedWarehouseIds } = useWarehouseContext();

  const { showCreate, showUpdate, showDelete, showError } = useMessageAlert();

  // Reference to product sizes hook so callers get access to product size options
  const productSizesHook = useProductSizes();
  const { productSizes, fetchProductSizes } = productSizesHook || {};

  // Deduplication & in-flight tracking
  const lastSignatureRef = useRef('');
  const lastFetchTimeRef = useRef(0);
  const inFlightRef = useRef(new Set());

  const generateSignature = useCallback((params) => JSON.stringify(params || {}), []);

  const shouldDedupe = useCallback((signature, force) => {
    if (force) return false;
    const now = Date.now();
    const timeDiff = now - lastFetchTimeRef.current;
    return signature === lastSignatureRef.current && timeDiff < 200;
  }, []);

  const fetchOrders = useCallback(async (params = {}, options = {}) => {
    const { force = false } = options;
    const signature = generateSignature({ ...params, warehouse_ids: selectedWarehouseIds });

    if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
    if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

    setLoading(true);
    setError(null);
    inFlightRef.current.add(signature);

    try {
      const result = await ordersService.getAllOrders(params, options);
      if (result.success) {
        const data = result.data;
        // Sort by updated_at or created_at (newest first)
        const sortByDate = (arr) => {
          return [...arr].sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at || 0);
            const dateB = new Date(b.updated_at || b.created_at || 0);
            return dateB - dateA; // Descending order (newest first)
          });
        };
        if (Array.isArray(data)) {
          setOrders(sortByDate(data));
          setTotalPages(1);
          setTotalCount(data.length);
        } else if (Array.isArray(data.items) || Array.isArray(data.orders) || Array.isArray(data.data)) {
          const arr = Array.isArray(data.items) ? data.items : (Array.isArray(data.orders) ? data.orders : data.data);
          setOrders(sortByDate(arr));
          setTotalPages(data.totalPages || Math.ceil((data.total || arr.length) / (params.limit || 20)));
          setTotalCount(data.total || arr.length);
        } else {
          // Unexpected shape, store as-is
          setOrders(data || []);
          setTotalPages(0);
          setTotalCount(Array.isArray(data) ? data.length : 0);
        }

        lastSignatureRef.current = signature;
        lastFetchTimeRef.current = Date.now();
      } else {
        // Keep the rows already on screen and say what happened (policy C19): a
        // transient failure used to blank the table as if there were no orders.
        setError(result.error);
        showError(result.error || i18n.t('hookErrors.common.unexpected'));
      }

      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      showError(errorMessage || i18n.t('hookErrors.common.unexpected'));
      return { success: false, error: errorMessage };
    } finally {
      inFlightRef.current.delete(signature);
      setLoading(false);
    }
  }, [generateSignature, shouldDedupe, selectedWarehouseIds]);

  const getOrderById = useCallback(async (orderId, options = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await ordersService.getOrderById(orderId, options);
      if (!result.success) setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const createOrder = useCallback(async (payload) => {
    setError(null);
    try {
      const normalized = normalizeOrderPayload(payload || {});

      // Prefer explicit service methods based on payload shape so the correct
      // endpoint is used (new-customer vs existing-customer).
      let result;
      if (payload && (payload.owner_name || payload.company_name)) {
        // payload looks like new-customer data
        result = await ordersService.createOrderForNewCustomer(normalized);
      } else if (payload && (payload.customer_id || payload.customerId)) {
        // existing customer order
        result = await ordersService.createOrderForExistingCustomer(normalized);
      } else {
        // fallback to generic createOrder which has its own heuristics
        result = await ordersService.createOrder(normalized);
      }
      if (result.success) {
        // Prepend newly created order
        setOrders(prev => [result.data, ...prev]);
        setTotalCount(prev => prev + 1);
        const label = result.data?.id || '';
        showCreate(i18n.t('orders.notifications.created', { id: label }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showCreate]);

  // Helper to invalidate/update window-level form cache after order changes
  const invalidateOrderCache = useCallback((orderId, updatedData = null) => {
    if (window.ordersFormCache && window.ordersFormCache.ordersById) {
      if (updatedData) {
        // Update the cache with new data
        window.ordersFormCache.ordersById[orderId] = updatedData;
      } else {
        // Just invalidate (delete) the cached entry
        delete window.ordersFormCache.ordersById[orderId];
      }
    }
  }, []);

  const updateOrder = useCallback(async (orderId, payload) => {
    setError(null);
    try {
      const normalized = normalizeOrderPayload(payload || {});
      const result = await ordersService.updateOrder(orderId, normalized);
      if (result.success) {
        setOrders(prev => prev.map(o => (String(o.id) === String(orderId) ? { ...o, ...result.data } : o)));
        // Invalidate the form cache so next edit shows fresh data
        invalidateOrderCache(orderId, result.data);
        showUpdate(i18n.t('orders.notifications.updated', { id: String(orderId) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate, invalidateOrderCache]);

  const confirmOrder = useCallback(async (orderId) => {
    setError(null);
    try {
      const result = await ordersService.confirmOrder(orderId);
      if (result.success) {
        setOrders(prev => prev.map(o => (String(o.id) === String(orderId) ? { ...o, ...result.data } : o)));
        invalidateOrderCache(orderId, result.data);
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [invalidateOrderCache]);

  const cancelOrder = useCallback(async (orderId) => {
    setError(null);
    try {
      const result = await ordersService.cancelOrder(orderId);
      if (result.success) {
        setOrders(prev => prev.map(o => (String(o.id) === String(orderId) ? { ...o, ...result.data } : o)));
        invalidateOrderCache(orderId, result.data);
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [invalidateOrderCache]);

  const updateOrderStatus = useCallback(async (orderId, statusPayload) => {
    setError(null);

    const requestedStatus = typeof statusPayload === 'string'
      ? statusPayload
      : statusPayload && (statusPayload.new_status || statusPayload.newStatus || statusPayload.status || statusPayload.status);

    // Snapshot the row's current status BEFORE the optimistic update so we can put
    // it back if the server rejects — otherwise the list keeps showing a status the
    // backend never accepted (the "UI lies" bug).
    let previousStatus;
    let hadRow = false;
    setOrders(prev => {
      const found = prev.find(o => String(o.id) === String(orderId));
      if (found) { previousStatus = found.status; hadRow = true; }
      return prev.map(o => (String(o.id) === String(orderId) ? { ...o, status: requestedStatus } : o));
    });
    const revertOptimistic = () => {
      if (!hadRow) return;
      setOrders(prev => prev.map(o => (String(o.id) === String(orderId) ? { ...o, status: previousStatus } : o)));
    };

    try {
      // For orders we call updateOrder with a small payload to change status
      const body = typeof statusPayload === 'string' ? { status: statusPayload } : statusPayload;
      const result = await ordersService.updateOrder(orderId, body);

      if (result.success) {
        if (result.data && typeof result.data === 'object' && Object.keys(result.data).length > 0) {
          setOrders(prev => prev.map(o => (String(o.id) === String(orderId) ? { ...o, ...result.data } : o)));
          // Invalidate the form cache so next edit shows fresh data
          invalidateOrderCache(orderId, result.data);
        } else {
          // Even without data, invalidate so next fetch gets fresh status
          invalidateOrderCache(orderId);
        }
        showUpdate(i18n.t('orders.notifications.statusUpdated', { id: String(orderId) }));
      } else {
        // Roll the row back to its real status on failure.
        revertOptimistic();
        setError(result.error);
      }
      return result;
    } catch (err) {
      revertOptimistic();
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate, invalidateOrderCache]);

  const deleteOrder = useCallback(async (orderId, options) => {
    setError(null);
    try {
      const result = await ordersService.deleteOrder(orderId, options);
      if (result.success) {
        setOrders(prev => prev.filter(o => String(o.id) !== String(orderId)));
        setTotalCount(prev => Math.max(0, prev - 1));
        // Remove from cache
        invalidateOrderCache(orderId);
        showDelete(i18n.t('orders.notifications.deleted', { id: String(orderId) }));
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete, invalidateOrderCache]);

  const bulkDeleteOrders = useCallback(async (ids, password, options) => {
    if (!Array.isArray(ids) || ids.length === 0) return { success: false, error: i18n.t('ordersModule.orders.errors.noSelection') };
    setError(null);
    try {
      const result = await ordersService.bulkDeleteSelectedOrders({ order_ids: ids, password }, options) || {};
      if (result.success) {
        setOrders(prev => prev.filter(o => !ids.includes(o.id)));
        setTotalCount(prev => Math.max(0, prev - ids.length));
        showDelete(formatBulkDeleteMessage(result, i18n.t('orders.notifications.bulkDeleted', { count: String(ids.length) })));
      } else {
        setError(result?.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  const deleteAllOrders = useCallback(async (password) => {
    setError(null);
    try {
      const result = await ordersService.bulkDeleteSalesOrders({ password });
      if (result.success) {
        setOrders([]);
        setTotalCount(0);
        showDelete(formatBulkDeleteMessage(result, `All Sales Orders and associated data deleted`));
      } else {
        setError(result?.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  const bulkDeleteAllGlobal = useCallback(async (password) => {
    setError(null);
    try {
      const result = await ordersService.bulkDeleteAllOrdersGlobal({ password });
      if (result.success) {
        setOrders([]);
        setTotalCount(0);
        showDelete(formatBulkDeleteMessage(result, `All Sales and Purchase Orders deleted successfully`));
      } else {
        setError(result?.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  // Delivery Challans wrappers
  const fetchDeliveryChallans = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await ordersService.getAllDeliveryChallans(params);
      if (!result.success) setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVehicleTracking = useCallback(async (params = {}, options = {}) => {
    const { force = false } = options;
    const signature = generateSignature({ ...params, warehouse_ids: selectedWarehouseIds, _type: 'vehicle-tracking' });

    if (shouldDedupe(signature, force)) return { success: true, fromCache: true };
    if (inFlightRef.current.has(signature)) return { success: true, fromCache: true };

    setLoading(true);
    setError(null);
    inFlightRef.current.add(signature);

    try {
      const result = await ordersService.getVehicleTracking(params);
      if (result.success) {
        setVehicleTracking(result.data.items || []);
        setVehicleTrackingTotal(result.data.total || 0);

        lastSignatureRef.current = signature;
        lastFetchTimeRef.current = Date.now();
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
      inFlightRef.current.delete(signature);
    }
  }, [generateSignature, shouldDedupe, selectedWarehouseIds]);

  const clearError = useCallback(() => setError(null), []);

  return {
    // State
    orders,
    loading,
    error,
    totalPages,
    totalCount,
    vehicleTracking,
    vehicleTrackingTotal,
    // Product sizes reference
    productSizes,
    fetchProductSizes,
    // Actions
    fetchOrders,
    getOrderById,
    createOrder,
    updateOrder,
    updateOrderStatus,
    cancelOrder,
    confirmOrder,
    deleteOrder,
    bulkDeleteOrders,
    deleteAllOrders,
    bulkDeleteAllGlobal,
    // Delivery actions
    fetchDeliveryChallans,
    fetchVehicleTracking,
    // Utils
    clearError,
  };
};

export default useOrders;
